import React from 'react';
import clsx from 'clsx';
import { Badge } from '@dopomogai/ui';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { ArticleSummary } from '@dopomogai/types';

export interface ArticleTeaserCardProps {
  article: ArticleSummary;
  onClick?: () => void;
  contextEyebrow?: string;
  actionLabel?: string;
  className?: string;
}

const formatPublishDate = (value?: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const ArticleTeaserCard: React.FC<ArticleTeaserCardProps> = ({
  article,
  onClick,
  contextEyebrow,
  actionLabel = 'Read',
  className,
}) => {
  const content = (
    <>
      <div className="aspect-[16/9] w-full overflow-hidden border-b-2 border-black bg-slate-50 relative">
        {article.heroImage ? (
          <img
            src={article.heroImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400 font-medium">
            No Image
          </div>
        )}
        {article.category ? (
          <div className="absolute top-4 left-4">
            <Badge text={article.category} className="bg-white text-black border border-black shadow-sm" />
          </div>
        ) : null}
      </div>

      <div className="flex flex-col flex-grow p-5 md:p-6">
        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-wider text-slate-500">
          {article.publishDate ? (
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={13} />
              {formatPublishDate(article.publishDate)}
            </span>
          ) : null}
          {article.publishDate && article.readTimeMinutes ? (
            <span className="h-1 w-1 rounded-full bg-slate-300" />
          ) : null}
          {article.readTimeMinutes ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} />
              {article.readTimeMinutes} min
            </span>
          ) : null}
        </div>

        {contextEyebrow ? (
          <div className="mb-3">
            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
              {contextEyebrow}
            </span>
          </div>
        ) : null}

        <h3 className="text-[1.4rem] md:text-[1.55rem] font-extrabold text-slate-900 mb-3 leading-[1.08] tracking-tight group-hover:text-emerald-600 transition-colors line-clamp-3">
          {article.title}
        </h3>

        <p className="text-[0.98rem] text-slate-600 line-clamp-2 mb-5 flex-grow leading-7 font-medium">
          {article.subtitle}
        </p>

        <div className="mt-auto pt-4 border-t-2 border-slate-100 group-hover:border-emerald-100 transition-colors">
          <span className="inline-flex items-center text-sm font-bold text-black group-hover:text-emerald-600 transition-colors uppercase tracking-wide">
            {actionLabel} <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </>
  );

  if (!onClick) {
    return (
      <article
        className={clsx(
          'group flex flex-col h-full bg-white rounded-xl overflow-hidden border-2 border-black',
          className,
        )}
      >
        {content}
      </article>
    );
  }

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`Open article: ${article.title}`}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      className={clsx(
        'group cursor-pointer flex flex-col h-full bg-white rounded-xl overflow-hidden border-2 border-black',
        'transition-all duration-200 ease-out transform',
        'hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgb(17,24,39)]',
        'focus:outline-none focus-visible:-translate-x-1 focus-visible:-translate-y-1 focus-visible:shadow-[6px_6px_0px_0px_rgb(17,24,39)]',
        className,
      )}
    >
      {content}
    </article>
  );
};

export default ArticleTeaserCard;
